import React, { createContext, useContext } from 'react'
import { LanguageISO6391 } from 'ai-constants'
import type {
  MessageTemplateDefinition,
  MessageTemplateType,
} from '../constants/message-template'

export const deliveryChannels = ['wsp', 'sms', 'email'] as const

export type DeliveryChannel = (typeof deliveryChannels)[number]

export const MessageTemplateStatuses = {
  ACTIVE: 'active',
  INACTIVE: 'inactive',
} as const

export type MessageTemplateStatus =
  (typeof MessageTemplateStatuses)[keyof typeof MessageTemplateStatuses]

export const messageTemplateStatusBySwitchChecked = {
  true: MessageTemplateStatuses.ACTIVE,
  false: MessageTemplateStatuses.INACTIVE,
} satisfies Record<`${boolean}`, MessageTemplateStatus>

export const messageTemplateSwitchCheckedByStatus = {
  [MessageTemplateStatuses.ACTIVE]: true,
  [MessageTemplateStatuses.INACTIVE]: false,
} satisfies Record<MessageTemplateStatus, boolean>

export interface IMessageTemplate {
  _id: string
  scope: 'company' | 'inspection_template'
  companyId: string
  userId: string
  status: MessageTemplateStatus
  gatewayId?: string
  gatewayStatus?: 'approved' | 'rejected' | 'pending'
  channel: DeliveryChannel
  template: MessageTemplateDefinition
  type?: MessageTemplateType
}

export type MessageTemplateCreateInput = Pick<
  IMessageTemplate,
  'template' | 'channel'
> & {
  type: MessageTemplateType
}

export type MessageTemplateUpdateInput = Pick<IMessageTemplate, 'template'> & {
  type: MessageTemplateType
}

export type MessageTemplateStatusUpdateInput = Pick<IMessageTemplate, 'status'>

export type MessageTemplateMutationResult =
  | { success: boolean }
  | { messageTemplates: IMessageTemplate[] }

export interface IMessageTemplateContext {
  multiple?: boolean
  buttonForegroundColor?: string
  buttonBackgroundColor?: string
  logo?: string
  deadlineDays?: number
  language?: LanguageISO6391
  channels: (typeof deliveryChannels)[number][]
  postMethod: (
    input: MessageTemplateCreateInput,
  ) => Promise<MessageTemplateMutationResult>
  updateMethod: (
    messageTemplateId: string,
    update: MessageTemplateUpdateInput,
  ) => Promise<MessageTemplateMutationResult>
  updateStatusMethod: (
    messageTemplateId: string,
    update: MessageTemplateStatusUpdateInput,
  ) => Promise<MessageTemplateMutationResult>
}

export const MessageTemplateCtx = createContext<
  IMessageTemplateContext | undefined
>(undefined)

export const useMessageTemplate = () => useContext(MessageTemplateCtx)!

export const MessageTemplateProvider: React.FC<
  React.PropsWithChildren<IMessageTemplateContext>
> = ({ children, ...rest }) => {
  return (
    <MessageTemplateCtx.Provider value={rest}>
      {children}
    </MessageTemplateCtx.Provider>
  )
}
